import { useState } from "react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useRecurringInvoices } from "@/features/invoices/hooks/useRecurringInvoices";

import CancelRecurringInvoiceDialog from "./CancelRecurringInvoiceDialog";
import RecurringInvoiceTable from "./RecurringInvoiceTable";

interface RecurringInvoiceSectionProps {
  onViewDetails: (id: string) => void;
  onEdit?: (id: string) => void;
}

const RecurringInvoiceSection = ({
  onViewDetails,
  onEdit,
}: RecurringInvoiceSectionProps) => {
  const [activeTab, setActiveTab] = useState<"active" | "ended">("active");
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedRecurringInvoiceId, setSelectedRecurringInvoiceId] = useState<
    string | null
  >(null);
  const [selectedCustomerName, setSelectedCustomerName] = useState<string>("");

  // Only fetch data for the currently active tab
  const { data: activeRecurringInvoices, isLoading: isLoadingActive } =
    useRecurringInvoices("active", { enabled: activeTab === "active" });
  const { data: endedRecurringInvoices, isLoading: isLoadingEnded } =
    useRecurringInvoices(["canceled", "completed"], {
      enabled: activeTab === "ended",
    });

  const handleCancel = (id: string, customerName: string) => {
    setSelectedRecurringInvoiceId(id);
    setSelectedCustomerName(customerName);
    setCancelDialogOpen(true);
  };

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
    setSelectedRecurringInvoiceId(null);
    setSelectedCustomerName("");
  };

  return (
    <>
      <div className="bg-[#f9f9f9] rounded-2xl p-4 md:p-6 mb-6">
        <div className="text-sm font-semibold text-[#767677] mb-4">
          Recurring Invoices
        </div>
        <div className="w-full">
          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as "active" | "ended")}
            className="w-full"
          >
            <TabsList className="mb-4 bg-transparent p-0 gap-4 h-auto border-b border-[#F1F1F1] w-full justify-start rounded-none">
              <TabsTrigger
                value="active"
                className="pb-2 pt-0 px-0 rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent data-[state=active]:shadow-none text-[#767677] data-[state=active]:text-black font-semibold text-sm transition-all"
              >
                Active
              </TabsTrigger>
              <TabsTrigger
                value="ended"
                className="pb-2 pt-0 px-0 rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent data-[state=active]:shadow-none text-[#767677] data-[state=active]:text-black font-semibold text-sm transition-all"
              >
                Ended
              </TabsTrigger>
            </TabsList>

            <div className="bg-[#fdfdfd] p-2">
              <TabsContent value="active" className="m-0">
                <RecurringInvoiceTable
                  recurringInvoices={activeRecurringInvoices || []}
                  isLoading={isLoadingActive}
                  onCancel={(id) => {
                    const invoice = activeRecurringInvoices?.find(
                      (inv) => inv.id === id,
                    );
                    if (invoice) {
                      handleCancel(id, invoice.customerName);
                    }
                  }}
                  onViewDetails={onViewDetails}
                  onEdit={onEdit}
                  showCancelButton={true}
                />
              </TabsContent>
              <TabsContent value="ended" className="m-0">
                <RecurringInvoiceTable
                  recurringInvoices={endedRecurringInvoices || []}
                  isLoading={isLoadingEnded}
                  onCancel={() => {}}
                  onViewDetails={onViewDetails}
                  showCancelButton={false}
                />
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </div>

      {selectedRecurringInvoiceId && (
        <CancelRecurringInvoiceDialog
          open={cancelDialogOpen}
          onOpenChange={handleCancelDialogClose}
          recurringInvoiceId={selectedRecurringInvoiceId}
          customerName={selectedCustomerName}
        />
      )}
    </>
  );
};

export default RecurringInvoiceSection;
